import { memo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { useClickAway } from 'react-use';
import {
  getSides,
  checkDoctorDept,
  getDragDropStyle,
  isHiddenInPrint,
  itemSettingStyle,
  itemTitleStyle,
  lineHeightStyle,
} from '../../../../utils/helpers';
import CommonDeleteBtn from '../commonDeleteBtn';
import SelectedItemSearch from './selectedItemSearch';

const ChiefComplain = ({
  element,
  prescriptionItems,
  setIsComplainModal,
  deletePrescriptionItem,
  selectedChiefComplains,
  setSelectedChiefComplains,
  isHistoryPage = false,
  isDelBtn = true,
}) => {
  const userInfo = useSelector((state) => state.authReducer.data);
  const specialties = useSelector(
    (state) => state.specialtyReducer.specialties,
  );
  const [isEdit, setIsEdit] = useState('');

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedItems = reorder(
      selectedChiefComplains,
      result.source.index,
      result.destination.index,
    );
    setSelectedChiefComplains(reorderedItems);
  };

  // this function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleExtras = (item, fieldName, val) => {
    const updatedComplains = selectedChiefComplains.map((complain) =>
      complain.name === item.name
        ? { ...complain, [fieldName]: val }
        : complain,
    );
    setSelectedChiefComplains(updatedComplains);
  };

  const ref = useRef(null);
  useClickAway(ref, (e) => {
    setIsEdit('');
  });

  const editMode = (item) => {
    return (
      <Row ref={ref} className="selected-row-edit" key={item.name}>
        <Col md="4" className="pl-0 pr-0">
          <SelectedItemSearch
            apiUrl="chief-complains"
            selectedItemName={item.name}
            selectedItems={selectedChiefComplains}
            selectedItemsAction={setSelectedChiefComplains}
          />
        </Col>
        <Col md="5">
          <Row>
            <Col md="4" className="pl-0 pr-0">
              <Form.Select
                className="form-control form-control-sm"
                defaultValue={item.side}
                onChange={(e) => handleExtras(item, 'side', e.target.value)}
              >
                <option value="">Side</option>
                {getSides(
                  checkDoctorDept(
                    'opthalmology',
                    userInfo?.speciality[0],
                    specialties,
                  ),
                ).map((side) =>
                  Object.entries(side).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )),
                )}
              </Form.Select>
            </Col>
            <Col md="3" className="pl-0 pr-0">
              <Form.Control
                size="sm"
                defaultValue={item.duration}
                min={0}
                type="number"
                placeholder="Duration"
                onChange={(e) => handleExtras(item, 'duration', e.target.value)}
                style={{ paddingRight: '0.2rem' }}
              />
            </Col>
            <Col md="5" className="pl-0">
              <Form.Select
                className="form-control form-control-sm"
                defaultValue={item.unit}
                onChange={(e) => handleExtras(item, 'unit', e.target.value)}
              >
                <option value={'hour(s)'}>Hour(s)</option>
                <option value={'day(s)'}>Day(s)</option>
                <option value={'week(s)'}>Week(s)</option>
                <option value={'month(s)'}>Month(s)</option>
                <option value={'year(s)'}>Year(s)</option>
                <option value={'long time'}>Long Time</option>
              </Form.Select>
            </Col>
          </Row>
        </Col>
        <Col md="3" className="pl-0">
          <Row>
            <Col md="10" className="pr-0 pl-0">
              <TextareaAutosize
                minRows={1}
                maxRows={3}
                defaultValue={item?.note}
                className="text-editor-edit"
                onChange={(e) => handleExtras(item, 'note', e.target.value)}
              />
            </Col>
            <Col md="1">
              <i
                className="fa fa-times-circle"
                style={{ paddingRight: '5px' }}
                aria-hidden="true"
                onClick={() => setIsEdit('')}
              ></i>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <div
      className={`row item-row ${
        selectedChiefComplains.length ? 'data-item' : 'no-data-item'
      }`}
      key={`chief-complain`}
    >
      <span
        className={`prescription-item ${
          isHiddenInPrint(prescriptionItems, 'chief-complain')
            ? 'item-title-hide'
            : ''
        }`}
        style={itemTitleStyle(
          prescriptionItems?.items?.['chief-complain']?.titleStyle || {},
        )}
        {...(!isHistoryPage
          ? {
              onClick: (e) => {
                e.stopPropagation();
                setIsComplainModal(true);
              },
            }
          : {})}
      >
        {element?.alterName?.length ? (
          <span>{element?.alterName}</span>
        ) : (
          <span>Chief Complain</span>
        )}
      </span>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <ul
              className="sub-child-list drag-drop"
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={lineHeightStyle('chief-complain', prescriptionItems)}
            >
              {selectedChiefComplains.map((item, index) => (
                <Draggable
                  key={item.name}
                  draggableId={item.name}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <li
                      key={index}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...getDragDropStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style,
                        ),
                        position: 'relative',
                      }}
                    >
                      <div onClick={() => setIsEdit(index)}>
                        <span
                          className="text-left pr-5"
                          style={itemSettingStyle(
                            prescriptionItems?.items?.['chief-complain']
                              ?.itemStyle || {},
                          )}
                        >
                          {item.name}{' '}
                          {item?.side ? ` - ${item.side.toUpperCase()}` : ''}
                          {item.duration !== '' || item.unit === 'long time'
                            ? ' - ' + item?.duration + ' ' + item?.unit
                            : ''}{' '}
                        </span>
                        {!isHistoryPage && (
                          <CommonDeleteBtn
                            action={deletePrescriptionItem}
                            itemName={'chiefComplain'}
                            delId={item.name}
                          />
                        )}
                      </div>
                      {item.note !== '' ? (
                        <div
                          style={itemSettingStyle(
                            prescriptionItems?.items?.['chief-complain']
                              ?.subItemStyle || {},
                          )}
                          className="pl-2 preline"
                        >
                          {item?.note}
                        </div>
                      ) : (
                        ''
                      )}
                      {index === isEdit && editMode(item)}
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default memo(ChiefComplain);
